import React, {useState, useEffect } from 'react';
import { useLocation, useHistory } from 'react-router-dom';
import { Row, Col, Card, CardHeader, CardBody, Form, FormGroup, Label, Input, Button } from 'reactstrap';
import Select from 'react-select';
import SweetAlert from 'react-bootstrap-sweetalert';

import { activateAuthLayout } from '../../../../store/actions';
import { connect } from 'react-redux';
import { gql } from 'apollo-boost';
import { useQuery, useMutation } from '@apollo/react-hooks';

const GET_DATA = gql`
	query getData($BundleId: ID) {
		Bundle(BundleId: $BundleId) {
			BundleId
			Name
			Description
			BundleItems {
				BundleId
				BundleItemId
				LinkQuoteItems
				ProductId
				Quantity
				Product {
					Name
					ProductCode
				}
			}
		}
		Products {
			ProductId
			ProductCode
			Name
			Description
			Active
		}
	}
`;

const CREATE_BUNDLE = gql`
	mutation createBundle($BundleInput: BundleInput) {
		createBundle(input: $BundleInput) {
			BundleId
			Name
			Description
		}
	}
`;

const UPDATE_BUNDLE = gql`
	mutation updateBundle($BundleInput: BundleInput) {
		updateBundle(input: $BundleInput) {
			BundleId
			Name
			Description
		}
	}
`;

const CREATE_BUNDLE_ITEMS = gql`
	mutation createBundleItems($BundleItemsList: [BundleItemsList], $BundleId: ID) {
		createBundleItems(input: $BundleItemsList, BundleId: $BundleId) {
			BundleId
			BundleItemId
			LinkQuoteItems
			Quantity
			ProductId
		}
	}
`;

const BundleForm = ({ activateAuthLayout, ...props }) => {
	useEffect(() => { activateAuthLayout(); }, [activateAuthLayout]);

	const location = useLocation();
	const history = useHistory();
	const currentQuoteParam = location.search && location.search.includes('?currentQuote=') ? `?currentQuote=${location.search.split('=').pop()}` : '';

	// initialize state 
	const [ init, setInit ] = useState(false);
	const [ formType ] = useState( location.pathname.match(/\/edit\/[\w\d\W]*/g) ? 'edit' : 'create' );
	const [ bundleDetails, setBundleDetails ] = useState({
		Name: '',
		Description: ''
	});
	const [ productOptions, setProductOptions ] = useState(null);
	const [ selectedProduct, setSelectedProduct ] = useState(null);
	const [ bundleItems, setBundleItems ] = useState([]);
	const [ formResult, setFormResult ] = useState(null);
	 
	// initialize apollo graphql queries and mutations
	const  { data }  = useQuery(GET_DATA, {
		variables: { BundleId: props.pathArg ? props.pathArg : 0 }
	});
	const [ createBundle, { data: createdBundleData } ] = useMutation(CREATE_BUNDLE);
	const [ updateBundle, { data: updatedBundleData } ] = useMutation(UPDATE_BUNDLE);
	const [ createBundleItems, { data: createdBundleItems } ] = useMutation(CREATE_BUNDLE_ITEMS);

	useEffect(()=>{
		if(createdBundleData && createdBundleData.createBundle && formResult === null  && createdBundleItems) {
			// show success modal when bundle was created
			setFormResult({
				success: true,
				message: 'Successfully created a new bundle'
			})
	   }

	   if(updatedBundleData && updatedBundleData.updateBundle && formResult === null && createdBundleItems) {
			// show success modal when bundle was updated
			setFormResult({
				success: true,
				message: 'Successfully updated bundle'
			})
		}
	}, [createdBundleData, formResult, createdBundleItems, updatedBundleData]);

	useEffect(()=>{
		if(data && data.Bundle && init === false) {
			// set bundle details and item on page initialization
			setBundleDetails({
				BundleId: data.Bundle.BundleId,
				Name: data.Bundle.Name,
				Description: data.Bundle.Description,
			})

			setBundleItems(data.Bundle.BundleItems.map(item => {
				return {
					Name: item.Product && item.Product.Name,
					ProductCode: item.Product?.ProductCode,
					Quantity: item.Quantity,
					ProductId: item.ProductId,
					LinkQuoteItems: item.LinkQuoteItems,
				}
			}));

			setInit(true);
		}
	}, [data, init]);

	useEffect(()=>{
		if(data && data.Products && productOptions === null) {
			// when products are loaded set product options
			const products = data.Products.filter(prod => prod.Active === 1).map(prod => { return { label: prod.Name, value: prod.ProductId, ProductCode: prod.ProductCode } });
			setProductOptions(products);
		}
	}, [data, productOptions]);

	const selectProduct = (prod) => {
		const productExists = bundleItems.filter(item => item.ProductId === prod.value).length > 0 ? true : false;
		if(productExists) {
			setFormResult({
				success: false,
				message: 'Sorry but product already exists.'
			})
		} else {
			const addedProduct = {
				ProductId: prod.value,
				ProductCode: prod.ProductCode,
				Name: prod.label,
				Quantity: 1
			}
			const list = Array.from(bundleItems);
			list.push(addedProduct);
			setBundleItems(list);
		}
		setSelectedProduct(null);
	}

	const updateBundleDetails = (value, field) => {
		const bundle= Object.assign({}, bundleDetails);
		bundle[field] = value;
		setBundleDetails(bundle);
	}

	const updateBundleItemEntry = (value, index, field) => {
		const items = Array.from(bundleItems);
		if (field === 'Quantity') {
			const qty = value === '' ? null : parseInt(value, 10);
			if (items[index].LinkQuoteItems) {
				items.forEach((item, i) => { if (item.LinkQuoteItems) items[i].Quantity = qty; });
			} else {
				items[index].Quantity = qty;
			}
		} else {
			items[index][field] = value;
			if (field === 'LinkQuoteItems' && value === 1) {
				const linkedQty = items.find((item, i) => i !== index && item.LinkQuoteItems)?.Quantity;
				if (linkedQty !== undefined) items[index].Quantity = linkedQty;
			}
		}
		setBundleItems(items);
	}

	const deleteBundleItem = (index) => {
		const items = Array.from(bundleItems);
		items.splice(index, 1);
		setBundleItems(items);
	}

	const saveBundle = (e) => {
		e.preventDefault();
		if(bundleDetails.Name) {
			const mappedItems = (bundleId) => bundleItems.map(item => ({
				BundleId: bundleId,
				ProductId: item.ProductId,
				Quantity: item.Quantity,
				LinkQuoteItems: item.LinkQuoteItems ? 1 : 0,
			}));
			if(formType === 'create') {
				createBundle({
					variables: { BundleInput: bundleDetails }
				}).then(({ data: { createBundle: newBundle } }) => {
					createBundleItems({
						variables: {
							BundleId: newBundle.BundleId,
							BundleItemsList: mappedItems(newBundle.BundleId),
						}
					});
				});
			} else if(formType === 'edit') {
				updateBundle({ variables: { BundleInput: bundleDetails } });
				createBundleItems({
					variables: {
						BundleId: bundleDetails.BundleId,
						BundleItemsList: mappedItems(bundleDetails.BundleId),
					}
				});
			}
		} else {
			setFormResult({
				success: false,
				message: 'Bundle Name is required.'
			})
		}
	}

	return (
		<React.Fragment>
			{ formResult && formResult.success &&
				<SweetAlert
					success
					title="Success"
					onConfirm={e => history.replace(`/admin/bundles${currentQuoteParam}`)}
					showConfirm={false}
					timeout={2500}
				>
				{ formResult.message }
				</SweetAlert>
			}
			{ formResult && formResult.success === false &&
				<SweetAlert
					error
					title="Error"
					onConfirm={e => setFormResult(null) } 
					showConfirm={true}
				>
				{ formResult.message }
				</SweetAlert>
			}
			{ data && ((data.Bundle === null && formType === 'create') || (data.Bundle && formType === 'edit')) &&
				<div className="content">
					<div className="container-fluid">
						<div className="page-title-box">
							<Row className="align-items-center">
								<Col sm="12">
									{ data &&
										<h4 className="page-title">{ formType === 'create' && data.Bundle === null ? 'Create Bundle' : `Edit Bundle - ${data.Bundle.Name}` }</h4>
									}
								</Col>
							</Row>
						</div>

						<Row>
							<Col lg="12">
								<Card>
									<CardHeader>Bundle Details</CardHeader>
									<CardBody>
										<Form>
											<FormGroup row>
												<Label sm={4}>Bundle Name <small className="text-danger">*</small></Label>
												<Col sm={8} className="text-right">
													<Input 
														type="text" 
														defaultValue={ data.Bundle && data.Bundle.Name ? data.Bundle.Name : ''} 
														onChange={e => updateBundleDetails(e.target.value, 'Name') } 
													/>
												</Col>
											</FormGroup>
											<FormGroup row>
												<Label sm={4}>Bundle Description</Label>
												<Col sm={8} className="text-right">
													<Input
														type="text"
														defaultValue={ data.Bundle && data.Bundle.Description ? data.Bundle.Description : ''}
														onChange={e => updateBundleDetails(e.target.value, 'Description') }
													/>
												</Col>
											</FormGroup>
   
										</Form>
									</CardBody>
								</Card>

								<Card> 
									<CardBody className="p-0">
										<div className="table-responsive">
											<table className="table">
												<thead className="thead-dark">
													<tr>
														<th>Product Code</th>
														<th>Product Name</th>
														<th>Quantity</th>
														<th></th>
													</tr>
												</thead>
												<tbody>
													{ bundleItems && bundleItems.length > 0 ? bundleItems.map((item,index)=>(
															<tr key={index}>
																<td>
																	{ item.ProductCode || item.ProductId }
																</td>
																<td>
																	{ item.Name }
																</td>
																<td>
																	<Input
																		type="number"
																		style={{width: '100px'}}
																		value={item.Quantity ?? ''}
																		onChange={ e => updateBundleItemEntry(e.target.value, index, 'Quantity') }
																	/>
																	<div className="display-checkboxes">
																		<label className="display-checkbox-wrapper">
																			<input
																				type="checkbox"
																				checked={!!item.LinkQuoteItems}
																				onChange={ e => updateBundleItemEntry(e.target.checked ? 1 : 0, index, 'LinkQuoteItems') }
																			/>
																			<span className="display-checkbox-text">Link quote items</span>
																		</label>
																	</div>
																</td>
																<td align="right">
																	<Button color="danger" onClick={e => deleteBundleItem(index) }>Delete</Button>
																</td>
															</tr>
														))
														:
														<tr className="text-center">
															<td colSpan={4}>No Bundle Items</td>
														</tr>
													}
													<tr>
														<td colSpan={4}>
															{ productOptions &&
																<Select
																	options={productOptions}
																	placeholder={'Add bundle product...'}
																	onChange={selectProduct}
																	value={selectedProduct}
																	filterOption={(option, inputValue) =>
																		option.label.toLowerCase().includes(inputValue.toLowerCase()) ||
																		option.data.ProductCode?.toLowerCase().includes(inputValue.toLowerCase())
																	}
																	menuPortalTarget={document.body}
																/>
															}
														</td>
													</tr>
												</tbody>
											</table>
										</div>
									</CardBody>
								</Card>

								<div className="text-lg-right">
									<Button color="primary" onClick={saveBundle}>Save Bundle</Button>
								</div>
							</Col>
						</Row>
				
					</div>
				</div>
			}

			{    
				// when data is loaded and bundle id is not valid show product does not exists error UI 
				data && data.Bundle === null && formType === 'edit' && 
					<div className="content">
						<div className="container-fluid">
							<div className="page-title-box">
								<Row className="align-items-center">
									<Col sm="6">
										<h4 className="page-title">Error loading this page</h4>
									</Col>
								</Row>
							</div>

							<Row>
								<Col lg="12">
									<Card>
										<CardBody>
											Bundle with ID {props.pathArg} does not exists
										</CardBody>
									</Card>
								</Col>
							</Row>
					
						</div>
					</div>
			}
		</React.Fragment>
	);
}

export default connect(null, { activateAuthLayout })(BundleForm);